import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import api, { refreshMemberSession, refreshMemberSessionIfNeeded } from '../utils/api';
import API from '../utils/apiconfig';
import { AuthContext } from './memberAuthContext';
import {
  clearMemberStorage,
  getMemberAccessToken,
  getMemberRefreshToken,
  isAuthFailure,
  isRequestCancelled,
  persistMemberTokens,
  readCachedMemberUser,
} from '../utils/authSession';
import { isAccessTokenExpired } from '../utils/tokenSession';
import { MEMBER_SESSION_EXPIRED_EVENT } from '../utils/sessionSync';

export { useAuth } from './useAuth';

const ADMIN_KEYS = ['admin_access_token', 'admin_refresh_token', 'admin_user'];

function clearAdminStorage() {
  ADMIN_KEYS.forEach((key) => localStorage.removeItem(key));
}

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => readCachedMemberUser());
  const [loading, setLoading] = useState(true);
  const bootstrapSeq = useRef(0);

  const clearMemberSession = useCallback(() => {
    clearMemberStorage();
    delete api.defaults.headers.common.Authorization;
    setUser(null);
  }, []);

  const persistUser = useCallback((userData) => {
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  }, []);

  const applyAccessToken = useCallback((access) => {
    if (access) {
      api.defaults.headers.common.Authorization = `Bearer ${access}`;
    }
  }, []);

  const fetchAndSetUser = useCallback(async () => {
    const res = await api.get(API.PROFILE, { skipGlobalLoading: true });
    persistUser(res.data);
    return res.data;
  }, [persistUser]);

  useEffect(() => {
    const runId = ++bootstrapSeq.current;

    const bootstrap = async () => {
      const token = getMemberAccessToken();
      const cached = readCachedMemberUser();

      if (cached) {
        setUser(cached);
      }

      if (!token) {
        if (cached) {
          clearMemberStorage();
          setUser(null);
        }
        if (bootstrapSeq.current === runId) {
          setLoading(false);
        }
        return;
      }

      let activeToken = token;
      if (isAccessTokenExpired(activeToken) && getMemberRefreshToken()) {
        try {
          const refreshed = await refreshMemberSession();
          if (refreshed) {
            activeToken = refreshed;
          }
        } catch {
          /* refreshMemberSession clears session on hard auth failures */
        }
      }

      applyAccessToken(activeToken);

      try {
        await fetchAndSetUser();
      } catch (err) {
        if (bootstrapSeq.current !== runId) return;
        if (isRequestCancelled(err)) return;
        if (isAuthFailure(err)) {
          clearMemberSession();
        }
      } finally {
        if (bootstrapSeq.current === runId) {
          setLoading(false);
        }
      }
    };

    bootstrap();
  }, [applyAccessToken, clearMemberSession, fetchAndSetUser]);

  useEffect(() => {
    if (!user || !getMemberRefreshToken()) return undefined;

    const keepAlive = async () => {
      const access = await refreshMemberSessionIfNeeded();
      if (access) {
        applyAccessToken(access);
      }
    };

    keepAlive();
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        keepAlive();
      }
    };
    const onFocus = () => {
      keepAlive();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    const interval = setInterval(keepAlive, 2 * 60 * 1000);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
      clearInterval(interval);
    };
  }, [user, applyAccessToken]);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === 'access_token') {
        if (event.newValue) {
          applyAccessToken(event.newValue);
        } else if (event.oldValue && !event.newValue) {
          clearMemberSession();
        }
      }
    };

    const onSessionExpired = () => {
      clearMemberSession();
      if (!window.location.pathname.startsWith('/login')) {
        navigate('/login', { replace: true, state: { reason: 'session_expired' } });
      }
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener(MEMBER_SESSION_EXPIRED_EVENT, onSessionExpired);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(MEMBER_SESSION_EXPIRED_EVENT, onSessionExpired);
    };
  }, [applyAccessToken, clearMemberSession, navigate]);

  const login = useCallback(
    async (tokens) => {
      clearMemberSession();
      clearAdminStorage();

      persistMemberTokens(tokens);
      applyAccessToken(tokens.access);

      await fetchAndSetUser();
    },
    [applyAccessToken, clearMemberSession, fetchAndSetUser]
  );

  const logout = useCallback(async (reason = 'Manual Logout') => {
    try {
      await api.post(
        API.LOGOUT,
        { reason },
        { skipGlobalLoading: true }
      );
    } catch {
      /* best-effort server log */
    }
    clearMemberSession();
  }, [clearMemberSession]);

  const updateUser = useCallback(
    (userData) => {
      persistUser(userData);
    },
    [persistUser]
  );

  const isAuthenticated = Boolean(user);

  return (
    <AuthContext.Provider
      value={{ user, login, logout, loading, updateUser, isAuthenticated }}
    >
      {children}
    </AuthContext.Provider>
  );
}
